import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { JobStatusResponse } from '../utils/api';
import { createPolyfilledWorkerUrl } from '../utils/pdfWorker';
import TransformationIndicator from './TransformationIndicator';

// Initialize the worker with polyfill
// This is called once when the module loads
let workerInitialized = false;
if (!workerInitialized) {
  createPolyfilledWorkerUrl().then((url) => {
    pdfjs.GlobalWorkerOptions.workerSrc = url;
    workerInitialized = true;
  });
}

interface PDFPreviewProps {
  file: File;
  onExtract: (selectedPages: number[]) => void;
  onReset: () => void;
  isExtracting: boolean;
  error: string | null;
  jobStatus: JobStatusResponse | null;
}

export default function PDFPreview({
  file,
  onExtract,
  onReset,
  isExtracting,
  error,
  jobStatus,
}: PDFPreviewProps) {
  const MAX_PAGES_SELECTION = 5;
  const [numPages, setNumPages] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showLimitHint, setShowLimitHint] = useState(false);

  // Reset state when file changes
  useEffect(() => {
    setNumPages(null);
    setIsLoading(true);
    setSelectedPages(new Set());
    setProcessingProgress(0);
    setProcessingStartTime(null);
    setLoadError(null);
  }, [file]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setLoadError(null);
    setProcessingProgress(0);
    setProcessingStartTime(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setIsLoading(false);
    setLoadError(
      'Failed to load PDF file. The file may be corrupted, password-protected, or in an unsupported format. Please try updating your browser or use a different PDF file.'
    );
  };

  const togglePageSelection = (pageNum: number) => {
    setSelectedPages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pageNum)) {
        newSet.delete(pageNum);
      } else {
        // Check if we've reached the maximum selection limit
        if (newSet.size >= MAX_PAGES_SELECTION) {
          return prev; // Don't add more pages if limit is reached
        }
        newSet.add(pageNum);
      }
      return newSet;
    });
  };

  const handleExtract = () => {
    const pagesArray = Array.from(selectedPages).sort((a, b) => a - b);
    setProcessingProgress(0);
    setProcessingStartTime(null);
    onExtract(pagesArray);
  };

  const hasSelectedPages = selectedPages.size > 0;
  const selectedPagesCount = selectedPages.size;
  const estimatedTimePerPage = 60000; // 1 minute in milliseconds
  // With parallel processing (15 workers), time is constant regardless of page count
  const totalEstimatedTime = estimatedTimePerPage;

  // Time ranges for display (threshold in seconds, display text)
  const timeRanges = [
    { max: 1, text: 'almost done' },
    { max: 5, text: '~5 seconds remaining' },
    { max: 10, text: '~10 seconds remaining' },
    { max: 15, text: '~15 seconds remaining' },
    { max: 30, text: '~30 seconds remaining' },
    { max: 60, text: '~1 minute remaining' },
    { max: 120, text: '~2 minute remaining' },
    { max: 180, text: '~3 minutes remaining' },
    { max: 240, text: '~4 minutes remaining' },
    { max: Infinity, text: '~5 minutes remaining' },
  ];

  // Function to format remaining time with rounded estimates
  const formatRemainingTime = (remainingSeconds: number): string => {
    return timeRanges.find(range => remainingSeconds <= range.max)?.text || '~5 minutes remaining';
  };

  // Update progress when processing
  useEffect(() => {
    if (jobStatus?.status === 'processing') {
      // Start the timer when processing begins (file upload is complete)
      if (!processingStartTime) {
        setProcessingStartTime(Date.now());
      }

      if (processingStartTime && selectedPagesCount > 0) {
        const interval = setInterval(() => {
          const elapsed = Date.now() - processingStartTime;
          const progress = Math.min((elapsed / totalEstimatedTime) * 100, 95); // Cap at 95% until actually complete
          setProcessingProgress(progress);
        }, 100); // Update every 100ms for smooth animation

        return () => clearInterval(interval);
      }
    } else if (jobStatus?.status === 'completed') {
      setProcessingProgress(100);
      setProcessingStartTime(null);
    } else if (jobStatus?.status === 'failed') {
      setProcessingProgress(0);
      setProcessingStartTime(null);
    }
  }, [jobStatus?.status, processingStartTime, selectedPagesCount, totalEstimatedTime]);

  const isSheetCompleted = jobStatus?.status === 'completed' && jobStatus.result && jobStatus.result.sheet_url;

  return (
    <div className="space-y-2 md:space-y-6">
      {/* Header with actions - Hidden when sheet is displayed */}
      {!isSheetCompleted && (
        <>

        {/* PDF Thumbnails Grid - Only show before extraction starts */}
        {!isExtracting && !jobStatus && (
          <div className="pb-6">
            {loadError ? (
              <div className="px-8p-6 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-red-800 text-base font-semibold mb-2">Unable to Display PDF</p>
                    <p className="text-red-700 text-sm mb-4">{loadError}</p>
                    <button
                      onClick={onReset}
                      className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white hover:from-red-700 hover:to-rose-700 font-semibold rounded-xl transition-all duration-200 focus:outline-none shadow-md hover:shadow-lg"
                    >
                      Try Another PDF
                    </button>
                  </div>
                </div>
              </div>
            ) : (
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              }
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : numPages ? (
                <div className='position-relative'>
                  <div className="flex md:flex-row flex-col items-center justify-between md:mb-6 mb-2 gap-2">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Select pages to extract
                    </h3>
                    <button
                      onClick={onReset}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium rounded-lg transition-colors focus:outline-none whitespace-nowrap"
                      aria-label="Upload a new PDF file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload New PDF
                    </button>
                  </div>
                  <p className="text-sm mb-6 text-gray-600">
                    <span
                      className={`
                        inline-block px-2 py-1 rounded-md font-semibold transition-all duration-200
                        ${showLimitHint
                          ? 'bg-orange-100 text-orange-600 scale-110 animate-[shake_0.5s_ease-in-out]'
                          : 'text-gray-700'
                        }
                      `}
                      style={showLimitHint ? {
                        animation: 'shake 0.5s ease-in-out',
                      } : {}}
                    >
                      {selectedPages.size}/{MAX_PAGES_SELECTION} pages selected
                    </span>
                    <span className="text-gray-400 mx-2">•</span>
                    <span className="text-gray-400">{numPages} in document</span>
                  </p>
                  <style>{`
                    @keyframes shake {
                      0%, 100% { transform: translateX(0) scale(1.1); }
                      20% { transform: translateX(-4px) scale(1.1); }
                      40% { transform: translateX(4px) scale(1.1); }
                      60% { transform: translateX(-4px) scale(1.1); }
                      80% { transform: translateX(4px) scale(1.1); }
                    }
                  `}</style>
                  <div className="md:max-h-[360px] max-h-[320px] overflow-y-auto -mx-8 px-8">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
                      const isSelected = selectedPages.has(pageNum);
                      const isDisabled = !isSelected && selectedPages.size >= MAX_PAGES_SELECTION;
                      return (
                        <div
                          key={pageNum}
                          onClick={() => {
                            if (isDisabled) {
                              setShowLimitHint(true);
                              setTimeout(() => setShowLimitHint(false), 1000);
                            } else {
                              togglePageSelection(pageNum);
                            }
                          }}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                              e.preventDefault();
                              togglePageSelection(pageNum);
                            }
                          }}
                          role="button"
                          tabIndex={isDisabled ? -1 : 0}
                          aria-label={`Page ${pageNum}${isSelected ? ' (selected)' : ''}${isDisabled ? ' (limit reached)' : ''}`}
                          aria-pressed={isSelected}
                          aria-disabled={isDisabled}
                          className={`
                            relative rounded-xl overflow-hidden
                            focus:outline-none
                            ${isSelected
                              ? 'cursor-pointer border-[3px] border-emerald-500 shadow-xl transition-all'
                              : isDisabled
                                ? 'cursor-not-allowed border-[3px] border-gray-200 opacity-50'
                                : 'cursor-pointer border-[3px] border-gray-300 hover:border-emerald-400 hover:shadow-lg transition-all'
                            }
                          `}
                        >
                          <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center">
                            <Page
                              pageNumber={pageNum}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              width={150}
                              className="shadow-sm"
                            />
                          </div>
                          {/* Selection indicator - shows for both selected and unselected */}
                          <div className="absolute top-2 right-2">
                            {isSelected ? (
                              <div className="bg-emerald-500 rounded-full p-1.5 border-2 border-white shadow-lg">
                                <svg
                                  className="w-4 h-4 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            ) : (
                              <div className="bg-white/80 rounded-full p-1.5 border-2 border-gray-300 shadow-md">
                                <svg
                                  className="w-4 h-4 text-transparent"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                          {/* Page number label */}
                          <div className={`
                            absolute bottom-0 left-0 right-0 py-2 px-2 text-center text-xs font-semibold
                            ${isSelected ? 'bg-emerald-500 text-white' : 'bg-gray-900/70 text-white'}
                          `}>
                            Page {pageNum}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                </div>
              ) : null}
            </Document>
            )}
          </div>
        )}
        
        {/* Job Status - Uploading, Processing and Completed (shown above button) */}
        {(isExtracting || (jobStatus && (jobStatus.status === 'processing' || (jobStatus.status === 'completed' && jobStatus.result)))) && (
          <div className="md:mt-6 mt-0 md:mb-6 mb-2">
            {/* Uploading informer - shown when extracting but no job status yet */}
            {isExtracting && !jobStatus && (
              <div className="p-5 bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl shadow-sm">
                <TransformationIndicator state="processing" />
                <div className="flex items-start gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-600 border-t-transparent flex-shrink-0 mt-0.5"></div>
                  <div className="flex-1">
                    <p className="text-emerald-900 text-base font-semibold">Uploading your PDF...</p>
                  </div>
                </div>
              </div>
            )}
            {jobStatus?.status === 'processing' && (
              <div className="p-5 bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl shadow-sm">
                <TransformationIndicator state="processing" />
                <div className="flex items-start gap-3 mb-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-600 border-t-transparent flex-shrink-0 mt-0.5"></div>
                  <div className="flex-1">
                    <p className="text-emerald-900 text-base font-semibold">Extracting your data...</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="w-full bg-white rounded-full h-2.5">
                    <div
                      className="bg-gradient-to-r from-emerald-500 to-green-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${processingProgress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Progress Text */}
                <div className="flex flex-col md:flex-row items-center justify-between text-sm text-emerald-700 font-medium">
                  <span>
                    {Math.round(processingProgress)}% complete
                  </span>
                  {processingStartTime && (
                    <span>
                      {(() => {
                        const elapsed = Date.now() - processingStartTime;
                        const remaining = Math.max(0, Math.ceil((totalEstimatedTime - elapsed) / 1000));
                        return formatRemainingTime(remaining);
                      })()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Extract button - Hidden when extraction is completed */}
        {jobStatus?.status !== 'completed' && (
        <button
          onClick={handleExtract}
              disabled={isExtracting || !hasSelectedPages}
          className={`
            w-full py-4 px-6 rounded-lg font-semibold text-white shadow-lg
            transition-all duration-200
                ${isExtracting || !hasSelectedPages
              ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600'
            }
                focus:outline-none mt-0
          `}
              aria-label={
                !hasSelectedPages 
                  ? 'Select pages to extract' 
                  : `Extract ${selectedPages.size} page${selectedPages.size > 1 ? 's' : ''} to Google Sheets`
              }
        >
          {isExtracting ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {jobStatus?.status === 'processing' ? 'Processing...' : 'Uploading...'}
            </span>
          ) : !hasSelectedPages ? (
            'Select pages to extract'
          ) : (
              <>
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm md:text-base">Extract {selectedPages.size} page{selectedPages.size > 1 ? 's' : ''} to Google Sheets</span>
                </span>
              </>
          )}
        </button>
        )}

        {/* Job Status - Pending and Failed (shown below button) */}
        {jobStatus && (jobStatus.status === 'pending' || jobStatus.status === 'failed' || (jobStatus.status === 'completed' && jobStatus.result?.error)) && (
          <div className="mt-6">
            {jobStatus.status === 'pending' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent flex-shrink-0 mt-0.5"></div>
                <div>
                  <p className="text-blue-800 text-sm font-medium">Job created</p>
                  <p className="text-blue-700 text-xs mt-1">Waiting to start processing...</p>
              </div>
              </div>
            )}
            {jobStatus.status === 'completed' && jobStatus.result?.error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                  <div>
                    <p className="text-red-800 text-sm font-semibold mb-1">
                    Failed to create Google Sheet
                    </p>
                    <p className="text-red-700 text-sm">{jobStatus.result.error}</p>
                  </div>
              </div>
            )}
            {jobStatus.status === 'failed' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <div>
                  <p className="text-red-800 text-sm font-semibold mb-1">Job failed</p>
                  <p className="text-red-700 text-sm">{jobStatus.error || 'An unexpected error occurred'}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-red-800 text-sm font-semibold mb-1">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}
      </>
      )}
    </div>
  );
}

